import React, { useState, useMemo } from "react";
import {
  CreditCard,
  Lock,
  CheckCircle,
  Loader2,
  Check,
} from "lucide-react";

// Plan verilerini burada tanımlıyoruz, gerçek uygulamada API'den gelebilir.
const plans = [
  {
    name: "Premium Üyelik",
    price: 0, // Örnek fiyat
    features: [
      "Sınırsız fotoğraf yükleme",
      "Kişiselleştirilmiş mekan önerileri",
      "Yapay zeka sesli anlatım",
      "Mekanları favorilere ekleme ve puanlama",
      "Özel destek hattı",
    ],
  },
];

// Fiyatlandırma ve özellikler kartı bileşeni
const PlanSummaryCard = ({ plan }) => {
  const kdvRate = 0.20; // %20 KDV oranı
  const subtotal = plan.price;
  const kdvAmount = subtotal * kdvRate;
  const total = subtotal + kdvAmount;

  return (
    <div className="bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-700 h-full">
      <h3 className="text-xl font-bold text-white mb-4">Ödeme Özeti</h3>
      <div className="bg-gray-900 rounded-xl p-4 mb-4 border border-gray-700">
        <h4 className="text-lg font-semibold text-white mb-2">{plan.name}</h4>
        <p className="text-gray-400">
          Aylık{" "}
          <span className="text-2xl font-bold text-indigo-400">
            {plan.price.toFixed(2)} TL
          </span>
        </p>
      </div>
      <div className="space-y-2 mb-6">
        {plan.features.map((feature, index) => (
          <div key={index} className="flex items-center text-gray-300">
            <Check size={18} className="text-green-400 mr-2" />
            <span className="text-sm">{feature}</span>
          </div>
        ))}
      </div>
      <div className="border-t border-gray-700 pt-4">
        <div className="flex justify-between text-gray-400 mb-2">
          <span>Paket Ücreti</span>
          <span className="font-semibold text-white">{subtotal.toFixed(2)} TL</span>
        </div>
        <div className="flex justify-between text-gray-400 mb-2">
          <span>KDV (%20)</span>
          <span className="font-semibold text-white">{kdvAmount.toFixed(2)} TL</span>
        </div>
        <div className="flex justify-between font-bold text-white text-lg pt-2 border-t border-gray-600 mt-4">
          <span>Toplam Tutar</span>
          <span>{total.toFixed(2)} TL</span>
        </div>
      </div>
    </div>
  );
};

const ProfessionalPaymentPage = () => {
  // Ödeme formunun durumunu yöneten state'ler
  const [cardNumber, setCardNumber] = useState("");
  const [cardName, setCardName] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [error, setError] = useState("");
  const [selectedPlan] = useState(plans[0]); // Varsayılan olarak ilk plan seçili

  const totalAmount = useMemo(() => {
    const kdvRate = 0.20;
    const price = selectedPlan.price;
    const kdvAmount = price * kdvRate;
    return (price + kdvAmount).toFixed(2);
  }, [selectedPlan]);

  // Form inputları için genel bir değişim yöneticisi
  const handleChange = (e, setter) => {
    setter(e.target.value);
  };

  // Kart numarasını biçimlendirme (4 hanede bir boşluk)
  const formatCardNumber = (value) => {
    return value
      .replace(/\s?/g, "")
      .replace(/(\d{4})/g, "$1 ")
      .trim();
  };

  // Ödeme işlemini simüle eden fonksiyon
  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setPaymentSuccess(false);

    // Basit validasyon kontrolü
    if (!cardNumber || !cardName || !expiryDate || !cvv) {
      setError("Lütfen tüm alanları doldurun.");
      return;
    }

    setIsLoading(true);

    // Gerçek bir API çağrısı yerine 2 saniyelik bir gecikme ile ödeme simülasyonu
    setTimeout(() => {
      setIsLoading(false);
      setPaymentSuccess(true);
      setCardNumber("");
      setCardName("");
      setExpiryDate("");
      setCvv("");
    }, 2000);
  };

  return (
    <div className="bg-gray-850 min-h-screen flex items-center justify-center p-4 font-sans antialiased">
      <div className="flex flex-col lg:flex-row gap-8 w-full max-w-6xl mx-auto">
        {/* Sol Sütun: Ödeme Formu */}
        <div className="flex-1 bg-gray-800 rounded-3xl p-6 sm:p-10 shadow-xl border border-gray-800">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-2 leading-tight">
              💳 Ödeme Bilgileri
            </h1>
            <p className="text-gray-400 text-lg">
              Güvenli ödeme sayfasında bilgilerinizi girerek aboneliğinizi başlatın.
            </p>
          </div>

          <div className="flex items-center justify-center gap-2 mb-8 text-green-400">
            <Lock size={20} />
            <span className="font-semibold">Güvenli ve Şifrelenmiş Ödeme</span>
          </div>

          {paymentSuccess && (
            <div className="bg-green-500/20 text-green-400 p-4 rounded-xl flex items-center gap-3 mb-6">
              <CheckCircle size={24} />
              <div>
                <h5 className="font-semibold">Ödeme Başarılı!</h5>
                <p className="text-sm">İşleminiz başarıyla tamamlandı. Teşekkürler!</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-500/20 text-red-400 p-4 rounded-xl flex items-center gap-3 mb-6">
              <div className="font-semibold">{error}</div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="card-number" className="block text-sm font-medium text-gray-400 mb-2">
                Kart Numarası
              </label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  id="card-number"
                  value={formatCardNumber(cardNumber)}
                  onChange={(e) => handleChange(e, setCardNumber)}
                  placeholder="0000 0000 0000 0000"
                  className="w-full bg-gray-800 text-white border border-gray-700 rounded-xl py-3 pl-12 pr-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 placeholder-gray-500"
                  maxLength="19"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="card-name" className="block text-sm font-medium text-gray-400 mb-2">
                Kart Üzerindeki İsim
              </label>
              <input
                type="text"
                id="card-name"
                value={cardName}
                onChange={(e) => handleChange(e, setCardName)}
                placeholder="Ad Soyad"
                className="w-full bg-gray-800 text-white border border-gray-700 rounded-xl py-3 px-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 placeholder-gray-500"
                required
              />
            </div>

            <div className="flex gap-4">
              <div className="flex-1">
                <label htmlFor="expiry-date" className="block text-sm font-medium text-gray-400 mb-2">
                  Son Kullanma Tarihi (AA/YY)
                </label>
                <input
                  type="text"
                  id="expiry-date"
                  value={expiryDate}
                  onChange={(e) => handleChange(e, setExpiryDate)}
                  placeholder="AA/YY"
                  className="w-full bg-gray-800 text-white border border-gray-700 rounded-xl py-3 px-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 placeholder-gray-500"
                  maxLength="5"
                  required
                />
              </div>
              <div className="flex-1">
                <label htmlFor="cvv" className="block text-sm font-medium text-gray-400 mb-2">
                  CVV
                </label>
                <input
                  type="text"
                  id="cvv"
                  value={cvv}
                  onChange={(e) => handleChange(e, setCvv)}
                  placeholder="***"
                  className="w-full bg-gray-800 text-white border border-gray-700 rounded-xl py-3 px-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 placeholder-gray-500"
                  maxLength="4"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 rounded-xl text-lg font-bold text-white bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 size={24} className="animate-spin" />
                  <span>Ödeme Yapılıyor...</span>
                </>
              ) : (
                <span>Toplam {totalAmount} TL ile Şimdi Öde</span>
              )}
            </button>
          </form>
        </div>

        {/* Sağ Sütun: Paket Özeti */}
        <div className="flex-1">
          <PlanSummaryCard plan={selectedPlan} />
        </div>
      </div>
    </div>
  );
};

export default ProfessionalPaymentPage;
